'use client'

import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useUserAgent } from '@marketplace-web/environment/request-context-data'
import { useSession } from '@marketplace-web/shared/session-data'
import { useSystemConfiguration } from '@marketplace-web/shared/system-configuration-data'

import {
  AdPage,
  AdPlatform,
  receivedAdEvent,
  getRoktTransactionsAttributes,
  transformRoktTransactionAttributes,
  RoktLauncherSelection,
  RoktEvent,
} from '@marketplace-web/ads/ads-data'

import AdsContext from '../../../containers/AdsProvider/AdsContext'
import { encryptEmail } from '../../../utils/email-encrypt'
import useAdLoadtimeLogging from '../../../hooks/useAdLoadtimeLogging'
import useRequestedAdLogging from '../../../hooks/useRequestedAdLogging'
import useShouldShowRokt from '../../../hooks/useShouldShowRokt'
import useShouldShowVanOffer from '../../../hooks/useShouldShowVanOffer'

const RoktAdvertisement = () => {
  const hasRun = useRef(false)
  const selection = useRef<RoktLauncherSelection | null>(null)

  const { user } = useSession()
  const { locale } = useIntl()
  const { userCountry: countryCode } = useSystemConfiguration() || {}
  const { transaction } = useConversationContext()
  const { getAdsPlatform } = useContext(AdsContext)
  const transactionId = transaction?.id
  const isDebitProcessed = transaction?.isDebitProcessed

  const isRoktScriptEnabled = useFeatureSwitch('web_ads_rokt_script')
  const isImpressionTrackingEnabled = useFeatureSwitch('web_ads_impression_tracking')

  const { setShouldShowRokt, shouldShowRokt, setRoktWasShown } = useShouldShowRokt()
  const { setShouldShowVan } = useShouldShowVanOffer()

  const trackingCode = `${countryCode}_web_successful_purchase_rokt`

  const { track } = useTracking()
  const userAgent = useUserAgent()
  const isSandbox =
    typeof window !== 'undefined' &&
    (window.location.host.includes('sandbox') || window.location.host.includes('localhost'))

  const adsPlatform = useMemo(() => getAdsPlatform(userAgent), [userAgent, getAdsPlatform])

  const { onRequest: onAdLoadtimeRequest, onLoad } = useAdLoadtimeLogging({
    countryCode,
    shape: 'Rokt',
    page: AdPage.Messages,
    platform: adsPlatform,
  })
  const { onRequest: onRequestedAdRequest } = useRequestedAdLogging({
    placementId: trackingCode,
  })

  const handleOnPlacementRequest = useCallback(() => {
    onAdLoadtimeRequest()
    onRequestedAdRequest()
  }, [onAdLoadtimeRequest, onRequestedAdRequest])

  const handleOnPlacementLoad = useCallback(() => {
    setRoktWasShown()

    if (isImpressionTrackingEnabled && countryCode) {
      track(
        receivedAdEvent({
          placementId: trackingCode,
          isMobileWeb: getAdsPlatform(userAgent) === AdPlatform.Mobile,
          countryCode,
        }),
      )
    }

    onLoad()
  }, [
    track,
    trackingCode,
    userAgent,
    onLoad,
    countryCode,
    isImpressionTrackingEnabled,
    getAdsPlatform,
    setRoktWasShown,
  ])

  const getRoktAttributes = useCallback(async () => {
    if (!user || !transactionId || !isDebitProcessed) return null

    const userEmail = user.email ? await encryptEmail(user.email) : undefined
    const userFirstName = user.real_name?.split(' ')[0]

    const attributes = {
      sandbox: isSandbox,
      placementid: trackingCode,
      customerid: user.id,
      emailsha256: userEmail,
      first_name: userFirstName,
      language: locale.toUpperCase(),
      country: user.country_iso_code.toUpperCase(),
    }

    const roktTransacitonAttributesResponse = await getRoktTransactionsAttributes({
      transactionId,
    })

    const errorsInRoktTransacitonAttributesResponse = 'errors' in roktTransacitonAttributesResponse

    if (!errorsInRoktTransacitonAttributesResponse) {
      Object.assign(
        attributes,
        roktTransacitonAttributesResponse
          ? transformRoktTransactionAttributes(roktTransacitonAttributesResponse)
          : {},
      )
    }

    return attributes
  }, [isSandbox, transactionId, trackingCode, locale, user, isDebitProcessed])

  const selectRoktPlacement = useCallback(async () => {
    const attributes = await getRoktAttributes()

    if (!window.RoktLauncher || !attributes) return

    setShouldShowRokt(false)
    setShouldShowVan(false)

    const launcher = await window.RoktLauncher

    handleOnPlacementRequest()

    selection.current = await launcher.selectPlacements({ attributes })
    selection.current.on(RoktEvent.PlacementReady).subscribe(handleOnPlacementLoad)
  }, [
    handleOnPlacementRequest,
    handleOnPlacementLoad,
    setShouldShowRokt,
    getRoktAttributes,
    setShouldShowVan,
  ])

  useEffect(() => {
    if (hasRun.current || !shouldShowRokt) return
    hasRun.current = true

    selectRoktPlacement()
  }, [selectRoktPlacement, shouldShowRokt])

  useEffect(() => {
    return () => {
      if (!selection.current) return

      selection.current.close()
    }
  }, [])

  if (!isRoktScriptEnabled) return null

  return <div id="rokt-placeholder" data-testid="rokt-test-placeholder" />
}

export default RoktAdvertisement
